import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChoiceChip } from "@/components/ui/choice-chip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FONTI_A_MANO, erroreDi } from "@/lib/crm";

/* Da dove arriva, a scelta multipla (0041, D12). `scelte` è sempre nell'ordine di
   FONTI_A_MANO: la prima è la fonte che conta. `bloccata` ({codice, label}) è la fonte
   messa dalla landing, che si vede accesa e non si toglie: le ads si misurano su quella. */
export default function FontiScelta({ scelte, onCambia, altro, onAltro, bloccata, legenda = "Da dove arriva" }) {
  const cambia = (codice) => {
    const dopo = scelte.includes(codice) ? scelte.filter((c) => c !== codice) : [...scelte, codice];
    onCambia(FONTI_A_MANO.map((f) => f.codice).filter((c) => dopo.includes(c)));
  };
  return (
    <fieldset className="grid gap-inside">
      <legend className="mb-inside text-body-strong text-ink">
        {legenda} <span className="font-normal text-ink-3">· anche più di una</span>
      </legend>
      <div className="flex flex-wrap gap-inside" data-testid="nuovo-cliente-fonti">
        {bloccata ? (
          <ChoiceChip pressed disabled data-testid="fonte-bloccata">
            {bloccata.label} · dalla landing
          </ChoiceChip>
        ) : null}
        {FONTI_A_MANO.filter((f) => f.codice !== bloccata?.codice).map((f) => (
          <ChoiceChip key={f.codice} pressed={scelte.includes(f.codice)} onPressedChange={() => cambia(f.codice)} data-testid={`fonte-${f.codice}`}>
            {f.label}
          </ChoiceChip>
        ))}
      </div>
      {scelte.includes("altro") ? (
        <div className="mt-inside grid gap-inside">
          <Label htmlFor="fonte-altro">Da dove, di preciso?</Label>
          <Input
            id="fonte-altro"
            placeholder="Per esempio: fiera di Lanciano"
            maxLength={200}
            value={altro}
            onChange={(e) => onAltro(e.target.value)}
            data-testid="fonte-altro-testo"
          />
        </div>
      ) : null}
    </fieldset>
  );
}

/** Le fonti di un cliente che c'è già, dalla scheda: la fonte della landing resta, le altre si scelgono. */
export function ModificaFontiDialog({ cliente, open, onOpenChange, onSalvato }) {
  const bloccata = cliente.fonte_automatica ? cliente.fonte_codice : null;
  const [scelte, setScelte] = useState([]);
  const [altro, setAltro] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setScelte((cliente.fonti || []).filter((c) => c !== bloccata));
      setAltro(cliente.fonte_altro || "");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const salva = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/crm/clienti/${cliente.id}`, { fonti: scelte, fonte_altro: altro.trim() || null });
      onOpenChange(false);
      onSalvato();
    } catch (err) {
      toast.error(erroreDi(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="modifica-fonti-modal">
        <form onSubmit={salva} className="grid gap-card">
          <DialogHeader>
            <DialogTitle>Da dove arriva</DialogTitle>
            <DialogDescription>
              {bloccata
                ? "La fonte della landing resta: le campagne si misurano su quella. Qui aggiungi le altre."
                : "La prima nell'elenco è quella che conta nel cruscotto, le altre si aggiungono."}
            </DialogDescription>
          </DialogHeader>
          <FontiScelta
            scelte={scelte}
            onCambia={setScelte}
            altro={altro}
            onAltro={setAltro}
            bloccata={bloccata ? { codice: bloccata, label: cliente.fonte_principale } : null}
            legenda="Le fonti"
          />
          <DialogFooter className="gap-inside">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={busy || (scelte.includes("altro") && !altro.trim())} data-testid="modifica-fonti-salva">
              {busy ? "Salvo…" : "Salva"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
